import React, { useMemo } from "react";
import { Menu, ExternalLink } from "lucide-react";
import { SiGmail, SiSlack, SiGithub, SiNotion, SiTrello } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface ConnectorsMenuProps {
  threadId?: string;
  selectedTools?: string[];
  onToolsChange?: (tools: string[]) => void;
  onConnect?: (connectorId: string) => void;
}

// Map tool names to icons and display names
const toolConfig: Record<
  string,
  { icon: React.ElementType; name: string; color: string }
> = {
  gmail: { icon: SiGmail, name: "Gmail", color: "#EA4335" },
  slack: { icon: SiSlack, name: "Slack", color: "#4A154B" },
  github: { icon: SiGithub, name: "GitHub", color: "#181717" },
  notion: { icon: SiNotion, name: "Notion", color: "#000000" },
  trello: { icon: SiTrello, name: "Trello", color: "#0079BF" },
};

export const ConnectorsMenu: React.FC<ConnectorsMenuProps> = ({
  threadId,
  selectedTools = [],
  onToolsChange,
}) => {
  const { integrationConnections } = useAuth();
  const { toast } = useToast();

  // Create connectors list from integration connections
  const connectors = useMemo(() => {
    if (!integrationConnections) return [];

    return integrationConnections.map((connection) => {
      const config = toolConfig[connection.tool] || {
        icon: SiGmail, // fallback icon
        name:
          connection.tool.charAt(0).toUpperCase() + connection.tool.slice(1),
        color: "#6B7280",
      };

      return {
        id: connection.tool,
        name: config.name,
        icon: config.icon,
        color: config.color,
        connected: connection.is_active,
        enabled: selectedTools.includes(connection.tool),
      };
    });
  }, [integrationConnections, selectedTools]);

  const handleToggle = async (toolId: string, enabled: boolean) => {
    const newSelectedTools = enabled
      ? [...selectedTools, toolId]
      : selectedTools.filter((tool) => tool !== toolId);

    // Notify parent component
    onToolsChange?.(newSelectedTools);

    // Save to database if we have a threadId
    if (threadId) {
      try {
        const { error } = await supabase
          .from("chat_threads")
          .update({ selected_tools: newSelectedTools })
          .eq("id", threadId);

        if (error) {
          console.error("Error updating selected tools:", error);
          toast({
            variant: "destructive",
            title: "Failed to save tool selection",
            description:
              "Could not save your tool selection. Please try again.",
          });
          return; // Don't update UI if database save failed
        }

        // Show success message
        toast({
          title: "Tool selection saved",
          description: `${toolId} has been ${enabled ? "enabled" : "disabled"} for this conversation.`,
        });
      } catch (error) {
        console.error("Error saving tool selection:", error);
        toast({
          variant: "destructive",
          title: "Failed to save tool selection",
          description: "An unexpected error occurred. Please try again.",
        });
        return; // Don't update UI if save failed
      }
    }
  };

  // Don't render if no connections available
  if (!connectors.length) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 rounded-full bg-white hover:bg-gray-50 flex items-center"
        >
          <Menu size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-3"
        align="start"
        side="top"
        sideOffset={8}
      >
        {/* Gmail Connector */}
        {connectors.map((connector) => (
          <button
            key={connector.id}
            onClick={() => handleToggle(connector.id, !connector.enabled)}
            className="w-full flex items-center justify-between px-2 py-2 rounded hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <connector.icon
                  className="h-4 w-4"
                  style={{ color: connector.color }}
                />
              </div>
              <span className="text-sm text-gray-900">{connector.name}</span>
            </div>
            <Switch
              checked={connector.enabled}
              onCheckedChange={(checked) => handleToggle(connector.id, checked)}
              onClick={(e) => e.stopPropagation()}
              className="data-[state=checked]:bg-blue-500"
            />
          </button>
        ))}

        {/* Bottom Links */}
        <div className="border-t border-gray-200 mt-3 pt-3 space-y-2">
          <a
            href="/settings/integrations"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors px-1"
          >
            Add connectors
            <ExternalLink size={12} />
          </a>
          <a
            href="/settings/integrations"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors px-1"
          >
            Manage connectors
            <ExternalLink size={12} />
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
};
